import React, { createContext, useRef, useCallback } from 'react'
import { useReducer } from 'react'
import { assetsForFile, actionForProvider } from '../components/Multipost/assetUtils'

const store = createContext({})

const { Provider } = store

const actions = {
  SET_ACCOUNTS: 'SET_ACCOUNTS',
  ADD_FILE: 'ADD_FILE',
  REMOVE_FILE: 'REMOVE_FILE',
  REPLACE_FILE: 'REPLACE_FILE',
  SET_MESSAGES: 'SET_MESSAGES',
  SET_SCHEDULED: 'SET_SCHEDULED',
  SET_SUBMITTING: 'SET_SUBMITTING',
}

const useEnhancedReducer = (reducer, initState, initializer) => {
  const lastState = useRef(initState)
  const getState = useCallback(() => lastState.current, [])
  return [
    ...useReducer(
      (state, action) => lastState.current = reducer(state, action),
      initState,
      initializer
    ),
    getState,
  ]
}

const reduce = (state, action) => {
  switch (action.type) {
    case actions.SET_ACCOUNTS:
      return {
        ...state,
        selectedAccounts: action.accounts,
      }
    case actions.ADD_FILE:
      if (state.selectedFiles?.find(f => f.key === action.file.key)) {
        return reduce(state, { type: actions.REPLACE_FILE, file: action.file })
      }
      return {
        ...state,
        selectedFiles: [...(state.selectedFiles || []), action.file],
      }
    case actions.REMOVE_FILE:
      return {
        ...state,
        selectedFiles: state.selectedFiles.filter(file => file.key !== action.file.key),
      }
    case actions.REPLACE_FILE:
      return {
        ...state,
        selectedFiles: state.selectedFiles.map(file => file.key === action.file.key ? action.file : file),
      }
    case actions.SET_MESSAGES:
      return {
        ...state,
        messages: action.messages,
      }
    case actions.SET_SCHEDULED:
      return {
        ...state,
        scheduled: action.scheduled,
      }
    case actions.SET_SUBMITTING:
      return {
        ...state,
        submitting: action.submitting,
      }
    default:
      return state
  }
}

const StateProvider = (props) => {
  let initialState = {}

  if(props.share.id) {
    initialState = {
      ...props,
      selectedAccounts: Object.keys(props.share?.metadata?.accounts || {}),
      selectedFiles: [],
      messages: {
        default: {
          value: props.share?.metadata?.messages?.default || '',
          overLength: false,
        },
      },
      scheduled: !!props.share.scheduled_at,
      submitting: false,
    }

    props.share.assets.forEach(file => {
      const shareAssets = props.share.metadata.files.filter(sa => sa.id.toString() === file.id.toString() && sa.type === file.type)
      const providers = new Set(shareAssets.flatMap(a => a.accounts))
      const fileAssets = assetsForFile(file, [...providers]).map(asset => ({ ...asset, enabledActions: [] }))

      // set each file's asset's enabledActions to match the metadata values
      shareAssets.forEach(shareAsset => {
        let key;
        switch(file.type) {
          case 'graphic':
            key = ["graphic", file.id, shareAsset.format].join('-')
            break;
          case 'upload':
            key = ["upload", file.id].join('-')
            break;
        }
        fileAssets.find(a => a.key === key).enabledActions = shareAsset.accounts.map(provider => actionForProvider(provider, shareAsset.story))
      })

      initialState.selectedFiles.push({
        ...file,
        assets: fileAssets,
        key: [file.type, file.id].join('-'),
      })
    })

    initialState.messages = props.socialAccounts?.reduce((messages, account) => {
      messages[account.attributes.provider] = {
        value: props.share?.metadata?.messages?.[account.attributes.provider] || '',
        overLength: false, // by definition, if we're editing then it was allowed to save and thus was not overLength
      }
      return messages
    }, initialState.messages)

  }else{
    initialState = {
      ...props,
      selectedAccounts: props.socialAccounts?.map(account => account.attributes.provider) || [],
      selectedFiles: [],
      messages: {
        default: {
          value: '',
          overLength: false,
        },
      },
      scheduled: false,
      submitting: false,
    }

    initialState.messages = props.socialAccounts?.reduce((messages, account) => {
      messages[account.attributes.provider] = {
        value: '',
        overLength: false,
      }
      return messages
    }, initialState.messages)
  }


  const [state, dispatch, getState] = useEnhancedReducer(reduce, initialState)

  const asyncDispatch = (action) => {
    const state = getState()
    switch (action.type) {
      default:
        return dispatch(action)
    }
  }

  const usableData = {
    ...state,
    twitterAccounts: state.socialAccounts?.filter(account => account.attributes.provider === 'twitter'),
    instagramAccounts: state.socialAccounts?.filter(account => account.attributes.provider === 'instagram'),
    facebookAccounts: state.socialAccounts?.filter(account => account.attributes.provider === 'facebook'),
  }

  const value = {
    ...usableData,
    dispatch: asyncDispatch.bind(usableData)
  }

  return <Provider value={value}>{props.children}</Provider>
}

export { store, StateProvider, actions }
